// src/tabs/logements/ServiceCatalogModal.jsx — Phase A.7.2b PR1 admin (ADR-122)
//
// Modal CRUD service catalog (create / edit / soft delete).
// C5 fonctionnel complet : formulaire selon type service + validation client.

import { useState, useEffect } from "react";
import { DS } from "../../lib/ui";
import { callServicesAction } from "../../lib/api-services-client";
import { autoTranslateServiceField } from "../../lib/services-autotranslate";

import useEchapPourFermer from "../../lib/useEchapPourFermer"
const VALID_TYPES = ["fixed", "per_day", "per_hour", "per_person", "distance_based"];
const VALID_WORKFLOWS = ["auto", "manual"];

function emptyForm() {
  return {
    id: null,
    slug: "",
    title_fr: "",
    title_en: "",
    title_es: "",
    description_fr: "",
    description_en: "",
    description_es: "",
    type: "fixed",
    price_eur: "",
    stock_total: "",
    workflow_validation: "manual",
    is_active: true,
    base_fee_eur: "",
    price_per_km_eur: "",
  };
}

export default function ServiceCatalogModal({ service, onClose, onSaved }) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)

  const isEdit = !!service?.id;
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Pré-remplit form si édition.
  useEffect(() => {
    if (isEdit && service) {
      const meta = service.tarification_metadata || {};
      setForm({
        id: service.id,
        slug: service.slug || "",
        title_fr: service.title?.fr || "",
        title_en: service.title?.en || "",
        title_es: service.title?.es || "",
        description_fr: service.description?.fr || "",
        description_en: service.description?.en || "",
        description_es: service.description?.es || "",
        type: service.type || "fixed",
        price_eur: service.price_eur_cents != null
          ? (service.price_eur_cents / 100).toString()
          : "",
        stock_total: service.stock_total != null
          ? service.stock_total.toString()
          : "",
        workflow_validation: service.workflow_validation || "manual",
        is_active: service.is_active !== false,
        base_fee_eur: meta.base_fee != null ? (meta.base_fee / 100).toString() : "",
        price_per_km_eur: meta.price_per_km != null ? (meta.price_per_km / 100).toString() : "",
      });
    } else {
      setForm(emptyForm());
    }
  }, [service, isEdit]);

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (error) setError(null);
  };

  const validateClient = () => {
    if (!isEdit) {
      if (!form.slug.trim()) return "Slug obligatoire.";
      if (!/^[a-z0-9_-]+$/.test(form.slug.trim()))
        return "Slug invalide (lettres minuscules, chiffres, _ ou -).";
      if (!form.title_fr.trim()) return "Titre FR obligatoire.";
      if (!form.type) return "Type obligatoire.";
      if (!form.price_eur) return "Prix obligatoire.";
    }
    if (form.price_eur !== "") {
      const p = parseFloat(form.price_eur);
      if (!Number.isFinite(p) || p < 0) return "Prix invalide (≥ 0).";
    }
    if (form.stock_total !== "") {
      const s = parseInt(form.stock_total, 10);
      if (!Number.isFinite(s) || s < 0) return "Stock invalide (entier ≥ 0).";
    }
    if (form.type === "distance_based") {
      if (form.base_fee_eur !== "" && parseFloat(form.base_fee_eur) < 0)
        return "Frais de base invalide.";
      if (form.price_per_km_eur !== "" && parseFloat(form.price_per_km_eur) < 0)
        return "Prix au km invalide.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateClient();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    // Auto-traduction AU SAVE (prérequis hub services) : remplit EN/ES VIDES
    // depuis le FR (titre + description). Jamais d'écrasement d'une saisie/
    // correction manuelle (cible non vide intacte), jamais bloquant (échec
    // Claude / méta-réponse filtrée → cible laissée vide → repli es→en→fr au
    // read). MÊME pattern que le livret (ADR-200).
    let trTitle;
    let trDesc;
    try {
      [trTitle, trDesc] = await Promise.all([
        autoTranslateServiceField({
          fr: form.title_fr,
          en: form.title_en,
          es: form.title_es,
          context: "title",
        }),
        autoTranslateServiceField({
          fr: form.description_fr,
          en: form.description_en,
          es: form.description_es,
          context: "content",
        }),
      ]);
    } catch (_) {
      // Défense en profondeur : ne jamais bloquer le save sur la traduction.
      trTitle = { en: form.title_en.trim(), es: form.title_es.trim() };
      trDesc = { en: form.description_en.trim(), es: form.description_es.trim() };
    }
    // Refléter dans l'UI (cohérence immédiate des champs auto-remplis).
    setForm((f) => ({
      ...f,
      title_en: trTitle.en,
      title_es: trTitle.es,
      description_en: trDesc.en,
      description_es: trDesc.es,
    }));

    const body = {
      title: {
        fr: form.title_fr.trim(),
        ...(trTitle.en ? { en: trTitle.en } : {}),
        ...(trTitle.es ? { es: trTitle.es } : {}),
      },
      description: {
        ...(form.description_fr.trim() ? { fr: form.description_fr.trim() } : {}),
        ...(trDesc.en ? { en: trDesc.en } : {}),
        ...(trDesc.es ? { es: trDesc.es } : {}),
      },
      type: form.type,
      workflow_validation: form.workflow_validation,
      is_active: form.is_active,
    };
    if (form.price_eur !== "") {
      body.price_eur_cents = Math.round(parseFloat(form.price_eur) * 100);
    }
    if (form.stock_total === "") {
      body.stock_total = null; // illimité
    } else {
      body.stock_total = parseInt(form.stock_total, 10);
    }
    if (!isEdit) {
      body.slug = form.slug.trim();
    } else {
      body.id = form.id;
    }
    if (form.type === "distance_based") {
      body.tarification_metadata = {
        base_fee: form.base_fee_eur !== "" ? Math.round(parseFloat(form.base_fee_eur) * 100) : 0,
        price_per_km: form.price_per_km_eur !== ""
          ? Math.round(parseFloat(form.price_per_km_eur) * 100)
          : 0,
      };
    }

    try {
      await callServicesAction("services_catalog_upsert", body);
      onSaved?.();
    } catch (e) {
      setError(e.message || "Erreur sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    setDeleting(true);
    setError(null);
    try {
      await callServicesAction("services_catalog_delete", { id: form.id });
      onSaved?.();
    } catch (e) {
      setError(e.message || "Erreur désactivation.");
      setDeleting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.5)",
        zIndex: 1000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="fbz-modal-sheet"
        style={{
          background: DS.white,
          borderRadius: 12,
          padding: 24,
          width: "min(620px, 92vw)",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 12px 40px rgba(0,0,0,.2)",
          fontFamily: DS.fontBody,
        }}
      >
        <h3
          style={{
            margin: 0,
            marginBottom: 16,
            fontFamily: DS.fontDisplay,
            fontSize: 20,
            color: DS.navy,
          }}
        >
          {isEdit ? "Modifier le service" : "Nouveau service catalog"}
        </h3>

        {error && (
          <div
            style={{
              padding: 10,
              background: "#fee2e2",
              color: DS.charge,
              borderRadius: 8,
              fontSize: 13,
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          {/* Slug (création seulement, lock en édition) */}
          {!isEdit && (
            <Field label="Slug (URL unique)" required>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => updateField("slug", e.target.value.toLowerCase())}
                placeholder="love-room"
                pattern="[a-z0-9_-]+"
                style={input()}
              />
            </Field>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="🇫🇷 Titre FR" required>
              <input
                type="text"
                value={form.title_fr}
                onChange={(e) => updateField("title_fr", e.target.value)}
                maxLength={200}
                style={input()}
              />
            </Field>
            <Field label="🇬🇧 Titre EN">
              <input
                type="text"
                value={form.title_en}
                onChange={(e) => updateField("title_en", e.target.value)}
                maxLength={200}
                style={input()}
              />
            </Field>
          </div>

          <Field label="🇪🇸 Titre ES">
            <input
              type="text"
              value={form.title_es}
              onChange={(e) => updateField("title_es", e.target.value)}
              maxLength={200}
              style={input()}
            />
          </Field>

          <p
            style={{
              margin: "-4px 0 0",
              fontSize: 11,
              color: DS.navyDark,
              opacity: 0.75,
              lineHeight: 1.4,
            }}
          >
            🇬🇧🇪🇸 laissés vides = traduits automatiquement depuis le 🇫🇷 à
            l'enregistrement (jamais d'écrasement d'une saisie manuelle).
          </p>

          <Field label="🇫🇷 Description FR">
            <textarea
              value={form.description_fr}
              onChange={(e) => updateField("description_fr", e.target.value)}
              maxLength={5000}
              rows={3}
              style={input()}
            />
          </Field>

          <Field label="🇬🇧 Description EN">
            <textarea
              value={form.description_en}
              onChange={(e) => updateField("description_en", e.target.value)}
              maxLength={5000}
              rows={3}
              style={input()}
            />
          </Field>

          <Field label="🇪🇸 Description ES">
            <textarea
              value={form.description_es}
              onChange={(e) => updateField("description_es", e.target.value)}
              maxLength={5000}
              rows={3}
              style={input()}
            />
          </Field>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Type tarification" required>
              <select
                value={form.type}
                onChange={(e) => updateField("type", e.target.value)}
                style={input()}
              >
                {VALID_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            {/* Le libellé disait « (non utilisé) » pour distance_based — et c'était
                vrai à l'envers : c'est ce prix unitaire qui était facturé, pendant
                que le forfait de base et le prix au km étaient ignorés. Depuis le
                lot « montant par type de tarification », un transfert se facture
                bien base + km × prix/km, et ce champ ne sert effectivement plus. */}
            <Field
              label={`Prix unitaire (€) ${form.type === "distance_based" ? "(non utilisé pour ce type)" : ""}`}
              required={!isEdit}
            >
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.price_eur}
                onChange={(e) => updateField("price_eur", e.target.value)}
                style={input()}
              />
            </Field>
          </div>

          {form.type === "distance_based" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, padding: 10, background: DS.bg, borderRadius: 8 }}>
              <Field label="Frais de base (€)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.base_fee_eur}
                  onChange={(e) => updateField("base_fee_eur", e.target.value)}
                  style={input()}
                />
              </Field>
              <Field label="Prix au km (€/km)">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.price_per_km_eur}
                  onChange={(e) => updateField("price_per_km_eur", e.target.value)}
                  style={input()}
                />
              </Field>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Stock total (vide = illimité)">
              <input
                type="number"
                min="0"
                value={form.stock_total}
                onChange={(e) => updateField("stock_total", e.target.value)}
                placeholder="∞"
                style={input()}
              />
            </Field>
            <Field label="Workflow validation">
              <select
                value={form.workflow_validation}
                onChange={(e) => updateField("workflow_validation", e.target.value)}
                style={input()}
              >
                {VALID_WORKFLOWS.map((w) => (
                  <option key={w} value={w}>
                    {w === "auto" ? "Auto (capture immédiate)" : "Manuel (admin valide)"}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="">
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => updateField("is_active", e.target.checked)}
              />
              Service actif (publié au voyageur)
            </label>
          </Field>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 20,
            gap: 10,
          }}
        >
          <div>
            {isEdit && (
              <>
                {!confirmDelete ? (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    style={{
                      padding: "9px 14px",
                      background: "transparent",
                      color: DS.charge,
                      border: `1px solid ${DS.charge}`,
                      borderRadius: 8,
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Désactiver service
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: DS.charge, fontWeight: 600 }}>
                      Confirmer ?
                    </span>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={deleting}
                      style={{
                        padding: "8px 14px",
                        background: DS.charge,
                        color: "#fff",
                        border: "none",
                        borderRadius: 8,
                        cursor: deleting ? "wait" : "pointer",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {deleting ? "..." : "Oui"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      style={{
                        padding: "8px 12px",
                        background: "#eef2f7",
                        color: "#444",
                        border: "none",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                    >
                      Non
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: "9px 18px",
                border: "none",
                borderRadius: 8,
                background: "#eef2f7",
                color: "#444",
                cursor: saving ? "wait" : "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "9px 22px",
                border: "none",
                borderRadius: 8,
                background: DS.navy,
                color: "#fff",
                cursor: saving ? "wait" : "pointer",
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {saving ? "Enregistrement..." : isEdit ? "Enregistrer" : "Créer"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      {label && (
        <label
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: DS.navyDark,
            marginBottom: 4,
          }}
        >
          {label} {required && <span style={{ color: DS.charge }}>*</span>}
        </label>
      )}
      {children}
    </div>
  );
}

function input() {
  return {
    width: "100%",
    padding: "8px 10px",
    border: `1px solid ${DS.border}`,
    borderRadius: 7,
    fontSize: 13,
    fontFamily: DS.fontBody,
    boxSizing: "border-box",
  };
}
